import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The shell must never show a blank screen (architecture §22). Anything that escapes a
 * component lands here and becomes a calm, recoverable screen: the child sees friendly words,
 * the parent gets the detail needed to report it.
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  #reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <main className="error-screen" role="alert">
        <h1>Something wobbled</h1>
        <p className="lede">
          Thrive Games hit a snag. Nothing was lost — press the button to head back and try again.
        </p>
        <p>
          <button type="button" className="button button--primary" onClick={this.#reset}>
            Try again
          </button>
        </p>
        <details>
          <summary>Details for a grown-up</summary>
          <pre>{error.message}</pre>
        </details>
      </main>
    );
  }
}
